import _ from 'lodash';
import { matchRoutePath } from '../libs/router/router-helpers';
import { getGameConfig } from '../../gameConfig';
import { stripPathLocale } from '../../common/localeShared';
import { LEGACY_LEFT_RAIL_MIN_VIEWPORT_WIDTH } from '../components/ads/adViewportThresholds';

// will be overridden with namedRoutes during init
let routesCache = {} as any;
export interface IAdvertisingService {
  init(state: any, namedRoutes: any): void;

  refreshDisplayAds(): void;

  cleanupPageAds(): void;

  onPageChanged(oldPath: string, newPath: string): void;
}

// we need to track which selector id created which unit id so we could properly destroy ads
// ideally PW would just return back (after an addUnits call) which IDs it gave each unit...
export const PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID: Record<string, string> = {};

type TPendingZoneProvider = 'aditude' | 'playwire';

type TPendingZone = {
  provider?: TPendingZoneProvider;
  zoneId: string;
  selectorId?: string | null;
  divId?: string | null;
  // Below-the-fold zones that must not open a pageview's auctions on their own.
  deferFirstAuction?: boolean;
};

type TQueueAditudeZoneOptions = {
  deferFirstAuction?: boolean;
};

const activeAditudeDivIds = new Set<string>();
// Zones already sent to the wrapper; at a route change these are carried-over chrome, not new inventory.
const auctionedAditudeDivIds = new Set<string>();

// A pageview whose only queued Aditude zones are deferred waits this long for above-the-fold
// zones before auctioning them alone, so pages without such zones still fill their footer.
export const ADITUDE_DEFERRED_ZONE_FLUSH_MS = 2500;
let hasLeadingAditudeAuctionRun = false;
let isFlushingDeferredAditudeZones = false;
let deferredAditudeFlushHandle: number | null = null;

function clearDeferredAditudeFlush() {
  if (deferredAditudeFlushHandle !== null) {
    window.clearTimeout(deferredAditudeFlushHandle);
    deferredAditudeFlushHandle = null;
  }
}

function resetAditudeAuctionOrdering() {
  hasLeadingAditudeAuctionRun = false;
  clearDeferredAditudeFlush();
}

function scheduleDeferredAditudeFlush() {
  if (deferredAditudeFlushHandle !== null) {
    return;
  }

  deferredAditudeFlushHandle = window.setTimeout(() => {
    deferredAditudeFlushHandle = null;
    isFlushingDeferredAditudeZones = true;
    try {
      advertisingService.refreshDisplayAds();
    } finally {
      isFlushingDeferredAditudeZones = false;
    }
  }, ADITUDE_DEFERRED_ZONE_FLUSH_MS);
}

function shouldHoldAditudeZones(aditudePendingZones: TPendingZone[]) {
  return (
    !hasLeadingAditudeAuctionRun &&
    !isFlushingDeferredAditudeZones &&
    aditudePendingZones.length > 0 &&
    aditudePendingZones.every((zone) => zone.deferFirstAuction)
  );
}

function getPendingZonesForProvider(rioPendingZones: TPendingZone[], provider: TPendingZoneProvider): TPendingZone[] {
  return rioPendingZones.filter((zone) => {
    if (zone.provider) {
      return zone.provider === provider;
    }

    // Keep compatibility with zones queued before provider ownership was explicit.
    return provider === 'aditude' ? Boolean(zone.divId) : !zone.divId;
  });
}

function removePendingZones(rioPendingZones: TPendingZone[], zonesToRemove: TPendingZone[]) {
  const removeSet = new Set(zonesToRemove);
  _.remove(rioPendingZones, (zone) => removeSet.has(zone));
}

export function queueAditudeZone(zoneId: string, divId: string, options: TQueueAditudeZoneOptions = {}) {
  const win = window as any;
  const rioPendingZones = (win.rioPendingZones = win.rioPendingZones || []);
  rioPendingZones.push({
    provider: 'aditude',
    zoneId,
    divId,
    ...(options.deferFirstAuction ? { deferFirstAuction: true } : {})
  });
  activeAditudeDivIds.add(divId);
}

export function destroyAditudeSlots(divIds: string[]) {
  const uniqueDivIds = Array.from(new Set(divIds.filter(Boolean)));
  if (uniqueDivIds.length === 0) {
    return;
  }

  const win = window as any;
  const rioPendingZones = win.rioPendingZones as TPendingZone[] | undefined;
  if (rioPendingZones) {
    const divIdSet = new Set(uniqueDivIds);
    _.remove(rioPendingZones, (zone) => Boolean(zone.divId && divIdSet.has(zone.divId)));
  }

  uniqueDivIds.forEach((divId) => {
    activeAditudeDivIds.delete(divId);
    auctionedAditudeDivIds.delete(divId);
  });

  const tude = (win.tude = win.tude || { cmd: [] });
  tude.cmd = tude.cmd || [];
  tude.cmd.push(() => {
    if (typeof tude.destroyAds === 'function') {
      // Keep React in control of the DOM while removing Aditude's GPT slots.
      tude.destroyAds(uniqueDivIds, false);
    } else {
      console.error('[Aditude] destroyAds is not available');
    }
  });
}

// this is the smallest sized of window that would fit a 300px width ad on left rail without overlap of content
const SMALLEST_WINDOW_WIDTH_FOR_LEFT_RAIL = LEGACY_LEFT_RAIL_MIN_VIEWPORT_WIDTH;

function rioQueueActiveZones() {
  const { rioPendingZones } = window as any as { rioPendingZones: TPendingZone[] };
  const zones = document.getElementsByClassName('rio-zone');
  rioPendingZones.length = 0;
  for (let i = 0; i < zones.length; ++i) {
    const zone = zones.item(i);
    if (zone) {
      const unitId = zone.getAttribute('data-rio-unit-id');
      if (unitId) {
        const selectorId = zone.getAttribute('data-rio-selector-id');
        const divId = zone.getAttribute('data-rio-div-id');
        if (divId) {
          // Chrome that already auctioned on the previous pageview waits for the new page's own zones,
          // so a navigation opens with one wrapper call instead of chrome first and content second.
          queueAditudeZone(unitId, divId, {
            deferFirstAuction:
              auctionedAditudeDivIds.has(divId) || zone.getAttribute('data-rio-defer-first-auction') === 'true'
          });
        } else {
          rioPendingZones.push({
            provider: 'playwire',
            zoneId: unitId,
            selectorId
          });
        }
      }
    }
  }
}

const ALLOWED_TAKEOVER_PATHS = ['/', '/addon', '/discord', '/realms', '/news', '/widgets'];

export function isAllowedTakeoverPath(path: string) {
  return ALLOWED_TAKEOVER_PATHS.includes(path);
}

const playwireHackRecreateTrendiVideoIfMissing = _.debounce(() => {
  const { ramp, __RIO_CONFIG } = window as any;
  if (__RIO_CONFIG.videoAdsProvider !== 'playwire') {
    return;
  }

  if (ramp && ramp.initCallbackCalled && ramp.destroyUnits && !document.getElementById('corner_ad_video')) {
    ramp.destroyUnits(['corner_ad_video']).then(() =>
      ramp.addUnits({ type: 'corner_ad_video' }).then(() => {
        ramp.displayUnits();
      })
    );
  }
}, 1500);

export function playwireDoChangePath(forcePath: string) {
  const { ramp } = window as any;
  if (!ramp) {
    // queue it up to be used as the default path
    (window as any).PLAYWIRE_DEFAULT_FORCEPATH = forcePath;
    return;
  }

  if (ramp.initCallbackCalled) {
    if (ramp.settings?.cp !== forcePath) {
      function doChangePath() {
        ramp.setPath(forcePath).catch(console.info.bind(null, `[ramp] setPath("${forcePath}") error`));
      }

      doChangePath();

      // disabling for now, because playwire bugs. when they fix trendi being unstable
      // then we want to do this so the video persists between path changes.
      // if (ramp.settings.slots.trendi_video) {
      //   document.getElementById('ramp_trendi_parent_container')?.remove();
      //   ramp
      //     .destroyUnits('trendi_video')
      //     .then(doChangePath)
      //     .then(() => ramp.addUnits({ type: 'trendi_video' }).then(() => ramp.displayUnits()))
      //     .catch((err) => console.error('playwireDoChangePath error', err));
      // } else {
      //   doChangePath();
      // }
    }
  } else {
    ramp.preInitSetPath = forcePath;
  }
}

export function changePlaywireForcePathBySitePath(path: string) {
  const strippedPath = stripPathLocale(path);
  if (!strippedPath || strippedPath === '/' || isAllowedTakeoverPath(strippedPath)) {
    playwireDoChangePath('takeover');
  } else {
    let found = false;

    const gameConfig = getGameConfig();
    if (gameConfig.getForcePathsFromRoutes) {
      for (const def of gameConfig.getForcePathsFromRoutes(routesCache)) {
        const match = matchRoutePath({ path: def.siteRoute.path, end: false }, strippedPath);
        if (match) {
          playwireDoChangePath(def.forcePath);
          found = true;
          break;
        }
      }
    }

    if (!found) {
      // revert back to the default forcepath
      playwireDoChangePath((window as any).PLAYWIRE_DEFAULT_FORCEPATH || '');
    }
  }
}

// called when page is actually changed
function internalTriggerPageChanged(path: string) {
  const { ramp, PageOS, Raven, handlePlaywirePageChanged } = window as any;
  const { displayAdsProvider = '', videoAdsProvider = '' } = (window as any).__RIO_CONFIG;
  if (ramp && (displayAdsProvider === 'playwire' || videoAdsProvider === 'playwire')) {
    changePlaywireForcePathBySitePath(path);
    playwireHackRecreateTrendiVideoIfMissing();

    if (PageOS) {
      // let PW know there was a virtual page change
      ramp.que.push(() => PageOS.session.newPageView());
    }

    if (handlePlaywirePageChanged) {
      ramp.que.push(() => handlePlaywirePageChanged());
    }
  }

  if (Raven && (displayAdsProvider === 'aditude' || videoAdsProvider === 'aditude')) {
    Raven.cmd.push(({ events }: any) => {
      events.pageview();
    });
  }
}

const debouncedPageChangeEvents = (oldPath: string, newPath: string) => {
  // clean up right away, as long as the page itself is changing - no need to debounce
  advertisingService.cleanupPageAds();
  resetAditudeAuctionOrdering();

  rioQueueActiveZones();
  advertisingService.refreshDisplayAds();
  internalTriggerPageChanged(newPath);
};

const playwireSkipSelectorUnits: { [key: string]: boolean } = {
  in_content: true,
  bottom_rail: true,
  left_rail: true,
  corner_ad_video: true,
  site_skin: true
};

// the "selector id" in playwire ends up being used as the actual DOM id, so we need
// it to be unique otherwise it will attempt to store multiple ad units to the same
// place in the DOM.
//
// JR 03/31/2023 - this is breaking ads on the site, so disabling
// let playwireUniqueSelectorId = 0;

function monitorBrowserSizeChanges(siteConfig: any) {
  if (siteConfig.displayAdsProvider !== 'playwire') {
    return;
  }

  // Add resize listener to update unit sizes
  window.addEventListener(
    'resize',
    _.debounce(() => {
      const { ramp } = window as any;
      if (!ramp || typeof ramp.destroyUnits !== 'function') {
        return;
      }

      if (window.innerWidth < SMALLEST_WINDOW_WIDTH_FOR_LEFT_RAIL) {
        // Destroy left-rail on resize. It will be re-added on navigation
        ramp.destroyUnits('pw-oop-left_rail');
      }
    }, 500)
  );
}

const advertisingService: IAdvertisingService = {
  init(state: any, namedRoutes: any) {
    routesCache = namedRoutes;

    Object.assign(window as any, {
      rioPendingZones: [],
      rioRefreshZones() {
        advertisingService.refreshDisplayAds();
      }
    });
    activeAditudeDivIds.clear();
    auctionedAditudeDivIds.clear();
    resetAditudeAuctionOrdering();

    // Own the initial provider pageview here so it follows the same path as SPA pageviews.
    internalTriggerPageChanged(window.location.pathname);

    monitorBrowserSizeChanges(state.siteConfig);
  },

  onPageChanged(oldPath: string, newPath: string) {
    // do not check/trigger the refreshes immediately. we need to wait until the page change actually occurs.
    debouncedPageChangeEvents(oldPath, newPath);
  },

  // called every time we want to refresh ad units on the page. not just called once per page load.
  refreshDisplayAds() {
    const { vmpbjs, ramp, nitroAds, rioPendingZones } = window as any as {
      vmpbjs: any;
      ramp: any;
      nitroAds: any;
      rioPendingZones: TPendingZone[];
    };
    if (!rioPendingZones || rioPendingZones.length === 0) {
      return;
    }

    const { displayAdsProvider = '', videoAdsProvider = '' } = (window as any).__RIO_CONFIG;
    const playwirePendingZones = getPendingZonesForProvider(rioPendingZones, 'playwire');

    if (
      ramp &&
      playwirePendingZones.length > 0 &&
      (displayAdsProvider === 'playwire' || videoAdsProvider === 'playwire')
    ) {
      const actions = () => {
        const units = playwirePendingZones.map((obj: any) => {
          const { zoneId, selectorId } = obj;
          return playwireSkipSelectorUnits[zoneId]
            ? { type: zoneId }
            : {
                selectorId: selectorId || `pw-placement-${zoneId}`,
                type: zoneId
              };
        });

        const beforeAddUnits = Object.keys(ramp.settings.slots);
        ramp
          .addUnits(units)
          .then((...data: any[]) => {
            // this is a hack to determine what unit ids are being created by PW, since
            // with their API if you add multiple of the same type they get a differen ID
            // that must be used when destroying it in the future.
            const afterAddUnits = Object.keys(ramp.settings.slots);
            const diffUnits = _.difference(afterAddUnits, beforeAddUnits);
            if (units.length === 1 && diffUnits.length === 1 && units[0].selectorId) {
              // we can only know for sure the 1:1 mapping for selectorId to PW unit id
              // when a single ad is being added at a time
              PW_HACK_MAP_SELECTOR_ID_TO_UNIT_ID[units[0].selectorId] = diffUnits[0];
            }
            ramp.displayUnits().catch(console.info.bind(null, '[ramp] failed to displayUnits', units));
          })
          .catch((err: any) => {
            console.error('[ramp] addUnits failed; still attempting displayUnits', units, err);
            ramp.displayUnits().catch(console.error.bind(null, '[ramp] failed to displayUnits', units));
          });

        removePendingZones(rioPendingZones, playwirePendingZones);
      };

      if (ramp.initCallbackCalled) {
        actions();
      } else {
        ramp.initCallback = actions;
      }
    }

    // other ad providers are just display, so they need to be checked independently
    // since it could be that we have playwire video + another provider's display
    if (vmpbjs && displayAdsProvider === 'venatus') {
      // as per their documentation we do not need to trigger refreshes
      // const actions = () => {
      //   const removeQueue = (window as any).__vm_remove = (window as any).__vm_remove || [];
      //   const addQueue = (window as any).__vm_add = (window as any).__vm_add || [];
      //   removeQueue.push(...uniqueZones);
      //   addQueue.push(...uniqueZones);
      //   rioPendingZones.length = 0;
      // };
      // actions();
    } else if (displayAdsProvider === 'freestar') {
      // React component handles ad creation/destruction via lifecycle
      // Just track page view for analytics on navigation
      const FreestarAdSlotClass = (window as any).FreestarAdSlot;
      if (FreestarAdSlotClass?.trackPageView) {
        FreestarAdSlotClass.trackPageView();
      }
      rioPendingZones.length = 0;
    } else if (nitroAds && displayAdsProvider === 'nitropay') {
      // nop - the placement is automatically recreating when location changes
    } else if (displayAdsProvider === 'mediavine') {
      // MediaVine auto-discovers mv_slot_target divs - no explicit refresh needed
      rioPendingZones.length = 0;
    } else if (displayAdsProvider === 'aditude') {
      const win = window as any;
      const tude = (win.tude = win.tude || { cmd: [] });
      tude.cmd = tude.cmd || [];
      const aditudePendingZones = getPendingZonesForProvider(rioPendingZones, 'aditude');
      if (shouldHoldAditudeZones(aditudePendingZones)) {
        scheduleDeferredAditudeFlush();
        return;
      }
      if (aditudePendingZones.length > 0) {
        hasLeadingAditudeAuctionRun = true;
        clearDeferredAditudeFlush();
      }

      const mappings = aditudePendingZones
        .map((obj: any) => {
          if (!obj.divId || !obj.zoneId) {
            console.error('[Aditude] missing divId or baseDivId for ad refresh', obj);
            return null;
          }

          return {
            divId: obj.divId,
            baseDivId: obj.zoneId
          };
        })
        .filter(Boolean);

      mappings.forEach((mapping: any) => auctionedAditudeDivIds.add(mapping.divId));

      if (mappings.length > 0) {
        tude.cmd.push(() => {
          const mountedMappings = mappings.filter((mapping: any) => document.getElementById(mapping.divId));
          if (mountedMappings.length === 0) {
            return;
          }

          if (typeof tude.refreshAdsViaDivMappings === 'function') {
            tude.refreshAdsViaDivMappings(mountedMappings);
          } else {
            console.error('[Aditude] refreshAdsViaDivMappings is not available');
          }
        });
      }

      removePendingZones(rioPendingZones, aditudePendingZones);
    }
  },

  // for some providers we need to cleanup ads on the page before navigating away
  cleanupPageAds() {
    const { ramp, vmpbjs } = window as any;
    const { displayAdsProvider = '' } = (window as any).__RIO_CONFIG;

    if (displayAdsProvider === 'aditude') {
      const currentAditudeDivIds = new Set(
        Array.from(document.querySelectorAll<HTMLElement>('[data-rio-div-id]'))
          .map((element) => element.dataset.rioDivId)
          .filter((divId): divId is string => Boolean(divId))
      );
      const staleAditudeDivIds = Array.from(activeAditudeDivIds).filter((divId) => !currentAditudeDivIds.has(divId));
      destroyAditudeSlots(staleAditudeDivIds);
    }

    let destroyPromise: Promise<any> | null = null;

    if (displayAdsProvider === 'playwire' && ramp?.initCallbackCalled && ramp.destroyUnits) {
      // wipe out all existing ads as the new ones will be re-inserted
      try {
        // HACK: ensure that corner_ad_video is the LAST item in the list, because for some reason
        // it may throw an exception and prevent the deletion of the units
        // TODO: maybe this is not really needed anymore?
        if (ramp.settings.slots.corner_ad_video) {
          const old = ramp.settings.slots.corner_ad_video;
          delete ramp.settings.slots.corner_ad_video;
          ramp.settings.slots.corner_ad_video = old;
        }

        // const unitsToDestroy = Object.keys(ramp.settings.slots).filter((id) => id !== 'trendi_video');
        // ramp.destroyUnits(unitsToDestroy);
        destroyPromise = ramp.destroyUnits('all');

        // ---> provided by playwire for how to clean up site skins on page change <---
        // https://raiderio.slack.com/archives/C02G5KZS2M7/p1678746389145599?thread_ts=1678730687.423759&cid=C02G5KZS2M7

        // remove the takeover
        const targetDivToRemoveLeadeboard = document.querySelectorAll('#pw-oop-flex_leaderboard_container');
        targetDivToRemoveLeadeboard.forEach((elem: any) => elem.remove());

        // cache Background Skin Container to be Removed
        const targetDivToRemoveBackground = document.querySelectorAll('#bgdiv');
        targetDivToRemoveBackground.forEach((elem: any) => elem.remove());

        // Remove takeover-active class on each page change
        document.documentElement.classList.remove('takeover-active');
        // <---- end
      } catch (err) {
        console.error('[ramp] failed to destroy units', err);
      }

      // cleanup flex leaderboard
      const bodyElement = document.getElementById('rio-body');
      if (bodyElement?.classList?.remove) {
        bodyElement.classList.remove('flex-leaderboard-active', 'flex-skin-active');
      }
      const flexContainer = document.getElementById('pw-oop-flex_leaderboard_container');
      if (flexContainer) {
        flexContainer.remove();
      }

      // re-add the in-content unit after destroy
      if (typeof destroyPromise === 'undefined') {
        destroyPromise = Promise.resolve();
      }

      // we want to re-add the in-content unit whenever a page changes
      const UNITS_TO_RECREATE_EACH_PAGE_CHANGE = [{ type: 'bottom_rail' }];
      if (window.innerWidth >= SMALLEST_WINDOW_WIDTH_FOR_LEFT_RAIL) {
        UNITS_TO_RECREATE_EACH_PAGE_CHANGE.push({ type: 'left_rail' });
      }

      destroyPromise!.then(() =>
        ramp.addUnits(UNITS_TO_RECREATE_EACH_PAGE_CHANGE).then(() => {
          ramp.displayUnits();
        })
      );
    } else if (vmpbjs) {
      const deferredRemovalQueue = (window as any).__vm_remove_deferred_raiderio || [];
      if (deferredRemovalQueue.length > 0) {
        const removeQueue = ((window as any).__vm_remove = (window as any).__vm_remove || []);
        console.info(`[VENATUS] executing deferred removal of ${deferredRemovalQueue.length} units`);
        for (const domNode of deferredRemovalQueue) {
          removeQueue.push(domNode);
        }
        deferredRemovalQueue.length = 0;
      }
    }
  }
};

export default advertisingService;
